import { ArrowRight, Ear, Radio } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { Badge } from '@reflex/ui/components/ui/badge';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { Card } from '@reflex/ui/components/ui/card';
import type { PluginWiring } from './types';

export function WebhookWiringCard({ wiring }: { wiring: PluginWiring }) {
  return (
    <Card className="p-4">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-muted-foreground" />
          {wiring.role === 'provider' ? (
            <>
              <PanelTitle as="span" size="sm">
                Provides
              </PanelTitle>
              <Badge variant="outline" className="text-xs font-normal">
                {wiring.source}
              </Badge>
              {wiring.route && (
                <span className="font-mono text-xs text-muted-foreground">{wiring.route}</span>
              )}
            </>
          ) : (
            <>
              <PanelTitle as="span" size="sm">
                Receives from
              </PanelTitle>
              <Badge variant="outline" className="text-xs font-normal">
                {wiring.source}
              </Badge>
              {wiring.providerPluginName && (
                <span className="text-xs text-muted-foreground">
                  via {wiring.providerPluginName}
                </span>
              )}
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-1">
          {wiring.events.map((e) => {
            const subscribed = (e as Record<string, unknown>)._subscribed;
            const isHighlighted = wiring.role === 'provider' || subscribed !== false;
            return (
              <Badge
                key={e.event}
                variant="secondary"
                className={cn('text-xs font-normal', !isHighlighted && 'text-muted-foreground')}
                title={e.description}
              >
                {e.event}
              </Badge>
            );
          })}
        </div>

        {wiring.role === 'provider' &&
          (wiring.receivers.length > 0 ? (
            <div className="space-y-1.5 border-l-2 border-border pl-6">
              {wiring.receivers.map((r) => (
                <div key={r.pluginName} className="flex min-w-0 items-center gap-2 text-xs">
                  <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <Ear className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <PanelTitle as="span" size="sm" className="text-xs">
                    {r.pluginName}
                  </PanelTitle>
                  <span className="text-muted-foreground">
                    listens to {r.events.map((e) => `${wiring.source}.${e}`).join(', ')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="pl-6 text-xs text-muted-foreground">No receivers registered</p>
          ))}
      </div>
    </Card>
  );
}
